/**
 * The row actions of a catalogue whose records are just a name (document
 * types, nationalities, KYC fields): rename in a small dialog, or delete after
 * confirming. The caller supplies the two requests; this owns the menu, both
 * dialogs, the notifications and the refresh.
 */

import React, { useState } from 'react';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { EditRounded as EditIcon, DeleteOutlineRounded as DeleteIcon } from '@mui/icons-material';
import { RowActionsMenu } from './RowActionsMenu';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';

export interface RenameRowActionsProps {
  /** The record's current name. */
  name: string;
  /** Label of the name field in the rename dialog. */
  nameLabel: string;
  /** Saves the new (trimmed) name. */
  onRename: (name: string) => Promise<unknown>;
  /** Deletes the record. */
  onDelete: () => Promise<unknown>;
}

export const RenameRowActions: React.FC<RenameRowActionsProps> = ({ name, nameLabel, onRename, onDelete }) => {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const t = useTranslate();

  const run = async (request: () => Promise<unknown>, doneMessage: string, close: () => void) => {
    setSaving(true);
    try {
      await request();
      notify(t(doneMessage, { smart_count: 1 }));
      refresh();
      close();
    } catch {
      notify(t('ra.notification.http_error'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!draft.trim()) return;
    run(() => onRename(draft.trim()), 'ra.notification.updated', () => setEditOpen(false));
  };

  return (
    <>
      <RowActionsMenu
        actions={[
          {
            key: 'edit',
            label: t('ra.action.edit'),
            icon: <EditIcon fontSize="small" />,
            onClick: () => { setDraft(name); setEditOpen(true); },
          },
          {
            key: 'delete',
            label: t('ra.action.delete'),
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            dividerBefore: true,
            onClick: () => setDeleteOpen(true),
          },
        ]}
      />

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth onClick={(e) => e.stopPropagation()}>
        <DialogTitle>{t('ra.action.edit')}</DialogTitle>
        <DialogContent>
          <TextField
            label={nameLabel}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            fullWidth
            autoFocus
            margin="dense"
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditOpen(false)} disabled={saving}>{t('ra.action.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !draft.trim()}>{t('ra.action.save')}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} name={name} onDelete={onDelete} />
    </>
  );
};

export default RenameRowActions;
